import { Select } from "@ews-admin/global-design-system";
import React from "react";
import { Control, Controller, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RoleEnum, RoleType } from "../../../../types/user.definition";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StaffFormData = any;

interface RoleSelectorProps {
  control: Control<StaffFormData>;
  setValue: UseFormSetValue<StaffFormData>;
  disabled?: boolean;
  isDoctorLimitReached?: boolean;
  isSecretaryLimitReached?: boolean;
  onUpgradeLicence?: () => void;
}

export const RoleSelector: React.FC<RoleSelectorProps> = ({
  control,
  setValue,
  disabled = false,
  isDoctorLimitReached = false,
  isSecretaryLimitReached = false,
  onUpgradeLicence,
}) => {
  const { t } = useTranslation();

  return (
    <div className="p-4 rounded-lg border-2 border-primary-200 bg-primary-50">
      <h4 className="mb-3 text-sm font-semibold text-primary-900">
        {t("staff.manage.form.selectStaffType")}
      </h4>
      <Controller
        name="roleName"
        control={control}
        render={({ field }) => (
          <>
            <Select
              required
              label={t("staff.manage.form.staffType")}
              options={[
                {
                  value: RoleEnum.DOCTOR,
                  label: isDoctorLimitReached
                    ? `${t("staff.manage.list.doctor")} (${t("staff.manage.form.limitReached")})`
                    : t("staff.manage.list.doctor"),
                  disabled: isDoctorLimitReached,
                },
                {
                  value: RoleEnum.SECRETARY,
                  label: isSecretaryLimitReached
                    ? `${t("staff.manage.list.secretary")} (${t("staff.manage.form.limitReached")})`
                    : t("staff.manage.list.secretary"),
                  disabled: isSecretaryLimitReached,
                },
              ]}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                setValue("roleName" as never, value as RoleType as never, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              disabled={disabled}
            />
            {disabled && (
              <p className="mt-2 text-xs text-neutral-600">
                {t("staff.manage.form.cannotChangeRole")}
              </p>
            )}
            {isDoctorLimitReached && !disabled && field.value === RoleEnum.DOCTOR && (
              <div className="flex items-center justify-between mt-3 p-3 bg-amber-50 rounded-md border border-amber-200">
                <p className="text-xs text-amber-800">
                  {t("staff.manage.form.doctorLimitReachedUpgrade")}
                </p>
                {onUpgradeLicence && (
                  <button
                    type="button"
                    onClick={onUpgradeLicence}
                    className="ml-3 shrink-0 text-xs font-medium text-amber-700 underline hover:text-amber-900"
                  >
                    {t("staff.manage.form.upgradeLicence")}
                  </button>
                )}
              </div>
            )}
            {isSecretaryLimitReached && !disabled && field.value === RoleEnum.SECRETARY && (
              <div className="flex items-center justify-between mt-3 p-3 bg-amber-50 rounded-md border border-amber-200">
                <p className="text-xs text-amber-800">
                  {t("staff.manage.form.secretaryLimitReachedUpgrade")}
                </p>
                {onUpgradeLicence && (
                  <button
                    type="button"
                    onClick={onUpgradeLicence}
                    className="ml-3 shrink-0 text-xs font-medium text-amber-700 underline hover:text-amber-900"
                  >
                    {t("staff.manage.form.upgradeLicence")}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      />
    </div>
  );
};
